'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface Collection {
  id: string;
  title: string;
  description: string;
  emoji: string;
  color: string;
  href: string;
  image?: string;
}

const collections: Collection[] = [
  {
    id: 'ecom-envelopes',
    title: 'Ecom Envelopes',
    description: 'Lightweight mailer envelopes',
    emoji: '📦',
    color: 'bg-amber-50',
    href: '/products/ecom-envelopes',
    image: '/assets/images/Ecom_envelopes_1-1787035102140.jpeg',
  },
  {
    id: 'kraft-boxes',
    title: 'Kraft Boxes',
    description: 'Sturdy kraft packaging boxes',
    emoji: '📫',
    color: 'bg-green-50',
    href: '/products/ecom-boxes',
    image: '/assets/images/Boxes-1787033569855.jpeg',
  },
  {
    id: 'cotton-bags',
    title: 'Cotton Bags',
    description: 'Cotton, tote & drawstring bags',
    emoji: '👜',
    color: 'bg-emerald-50',
    href: '/products/tote-bags',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__11_31_58-1787035942148.jpeg',
  },
  {
    id: 'glass-jars',
    title: 'Glass Jars',
    description: 'Premium glass packaging',
    emoji: '🫙',
    color: 'bg-teal-50',
    href: '/products/glass-jars',
    image: '/assets/images/glass_jars-1787033603820.jpeg',
  },
  {
    id: 'monocartons',
    title: 'Monocartons',
    description: 'Tags, stickers & thank you cards',
    emoji: '🗂️',
    color: 'bg-lime-50',
    href: '/products/monocartons',
    image: '/assets/images/Monocartons-1787033603916.jpeg',
  },
  {
    id: 'seed-paper',
    title: 'Seed Paper',
    description: 'Plantable eco-friendly paper',
    emoji: '🌱',
    color: 'bg-green-50',
    href: '/products/seed-notepad',
    image: '/assets/images/seed_paper_sheet-1787033603983.jpeg',
  },
];

export default function CollectionsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 lg:py-16 px-6 lg:px-12 max-w-7xl mx-auto" id="products">
      <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h2
          className="text-2xl md:text-3xl font-bold text-primary-dark mb-8"
          style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}
        >
          Collections
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {collections.map((col, i) => (
            <Link
              key={col.id}
              href={col.href}
              className={`collection-card group block rounded-card overflow-hidden border border-gray-100 hover:border-primary/40 transition-all duration-300 hover:shadow-card`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <div className={`${col.color} aspect-square flex items-center justify-center relative overflow-hidden`}>
                {col.image ? (
                  <img
                    src={col.image}
                    alt={col.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <span className="text-5xl collection-card-img group-hover:scale-110 transition-transform duration-500">
                    {col.emoji}
                  </span>
                )}
              </div>
              <div className="p-3 bg-white">
                <h3
                  className="text-sm font-semibold text-gray-800 leading-tight hover-arrow"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {col.title}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5 font-normal">{col.description}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-6 text-center">
          <a
            href="#products"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark transition-colors duration-200 border-b-2 border-primary/30 pb-0.5"
          >
            View all products
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
